import { MainApi } from 'api/MainApi'
import clsx from 'clsx'
import ActionsMenu, { ActionsMenuItem } from 'components/ActionsMenu/ActionsMenu'
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog'
import { format } from 'date-fns'
import { canDeleteRobotMap, useUser } from 'hooks/user'
import { ReactComponent as RemoveSvg } from 'images/icons/delete.svg'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { IRobotMap } from 'utils/interfaces'

import RenameRobotMapModal from './RenameRobotMapModal/RenameRobotMapModal'

import style from './RobotMapTable.module.css'

const DownloadIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" {...props}>
    <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" />
  </svg>
)

const UploadIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" {...props}>
    <path d="M9 16h6v-6h4l-7-7-7 7h4v6zm-4 2v2h14v-2H5z" />
  </svg>
)

const EraseIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" {...props}>
    <path d="M15.14,3C14.63,3 14.12,3.2 13.73,3.59L2.59,14.73C1.81,15.5 1.81,16.77 2.59,17.56L5.03,20H12.69L21.41,11.27C22.2,10.5 22.2,9.23 21.41,8.44L16.56,3.59C16.17,3.2 15.65,3 15.14,3M17,18L15,20H22V18" />
  </svg>
)

const RestoreIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" {...props}>
    <path d="M13 3c-4.97 0-9 4.03-9 9H1l3.89 3.89.07.14L9 12H6c0-3.87 3.13-7 7-7s7 3.13 7 7-3.13 7-7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42C8.27 19.99 10.51 21 13 21c4.97 0 9-4.03 9-9s-4.03-9-9-9z" />
  </svg>
)

interface RobotMapTableProps {
  robotMaps: IRobotMap[]
  selectedMapId?: number
  onMapSelect: (map: IRobotMap) => void
  onEditMap: (map: IRobotMap) => void
  onDeleteMap: (map: IRobotMap) => void
  onMapsChanged: () => void
  facilitiesMap: Record<number, string>
  currentPage: number
  pageSize: number
}

const RobotMapTable = ({
  robotMaps,
  selectedMapId,
  onMapSelect,
  onEditMap,
  onDeleteMap,
  onMapsChanged,
  facilitiesMap,
  currentPage,
  pageSize,
}: RobotMapTableProps) => {
  const { t } = useTranslation()
  const user = useUser()
  const canDeleteMap = canDeleteRobotMap(user?.state ?? { audience: null })

  const [mapToDelete, setMapToDelete] = useState<IRobotMap | undefined>(undefined)
  const [mapToRename, setMapToRename] = useState<IRobotMap | undefined>(undefined)
  const [mapToRestore, setMapToRestore] = useState<IRobotMap | undefined>(undefined)

  const fileInputRef = useRef<HTMLInputElement>(null)
  const [activeMapForUpload, setActiveMapForUpload] = useState<IRobotMap | undefined>(undefined)
  const [isProcessing, setIsProcessing] = useState(false)

  const handleDownload = async (map: IRobotMap) => {
    try {
      setIsProcessing(true)
      await MainApi.downloadRobotMap(map.id, map.name)
    } catch (err) {
      console.error(err)
      alert(t('robot_map.download_failed') || 'Failed to download map file')
    } finally {
      setIsProcessing(false)
    }
  }

  const handleUploadClick = (map: IRobotMap) => {
    setActiveMapForUpload(map)
    fileInputRef.current?.click()
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !activeMapForUpload) return

    try {
      setIsProcessing(true)
      await MainApi.uploadRobotMap(activeMapForUpload.id, file)
      onMapsChanged()
    } catch (err) {
      console.error(err)
      alert(t('robot_map.upload_failed') || 'Failed to upload map file')
    } finally {
      setIsProcessing(false)
      setActiveMapForUpload(undefined)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handleRestoreClick = (map: IRobotMap) => {
    setMapToRestore(map)
  }

  const handleConfirmRestore = async () => {
    if (!mapToRestore) return
    const map = mapToRestore
    setMapToRestore(undefined)

    try {
      setIsProcessing(true)
      await MainApi.restoreRobotMap(map.id)
      onMapsChanged()
    } catch (err) {
      console.error(err)
      alert(t('robot_map.restore_failed') || 'Failed to restore original map')
    } finally {
      setIsProcessing(false)
    }
  }

  return (
    <div className={style.tableWrapper}>
      <input
        type="file"
        ref={fileInputRef}
        accept=".tif,.tiff"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
      <div className={style.tableHeader}>
        <span></span>
        <div className={style.tableHeaderItem}>#</div>
        <div className={style.tableHeaderItem}>Facility</div>
        <div className={style.tableHeaderItem}>Name</div>
        <div className={style.tableHeaderItem}>Recorded at</div>
        <span></span>
        <span></span>
      </div>
      <div className={style.table}>
        {robotMaps.length > 0 ? (
          robotMaps.map((map, index) => (
            <div
              key={map.id}
              className={clsx(style.tableRow, { [style.highlighted]: selectedMapId === map.id })}
              onClick={() => onMapSelect(map)}
            >
              <span></span>
              <span className={style.tableColLeft}>{(currentPage - 1) * pageSize + index + 1}</span>
              <span className={style.tableColLeft}>
                {map.facility_id ? facilitiesMap[map.facility_id] : (map as any).facility_name || 'N/A'}
              </span>
              <span className={style.tableColLeft}>{map.name}</span>
              <span className={clsx(style.tableCol, style.recordedAtCol)}>
                <span>{format(new Date(map.createdAt), 'dd.MM.yyyy HH:mm')}</span>
              </span>
              <span className={style.statusCol}>
                {map.is_processing ? (
                  <span className={style.spinner} title={t('robot_map.processing') || 'Processing occupancy map...'} />
                ) : (
                  map.is_edited && <span className={style.editedDot} title={t('robot_map.edited') || 'Edited map'} />
                )}
              </span>
              <div className={style.actionsCol} onClick={e => e.stopPropagation()}>
                <ActionsMenu>
                  <ActionsMenuItem onClick={() => handleDownload(map)} disabled={isProcessing}>
                    <DownloadIcon className={style.menuItemIcon} />
                    <span className={style.menuItemText}>{t('common.download') || 'Download'}</span>
                  </ActionsMenuItem>
                  <ActionsMenuItem onClick={() => handleUploadClick(map)} disabled={isProcessing}>
                    <UploadIcon className={style.menuItemIcon} />
                    <span className={style.menuItemText}>
                      {t('robot_map.upload_occupancy') || 'Upload occupancy map'}
                    </span>
                  </ActionsMenuItem>
                  <ActionsMenuItem onClick={() => onEditMap(map)} disabled={isProcessing}>
                    <EraseIcon className={style.menuItemIcon} />
                    <span className={style.menuItemText}>{t('robot_map.erase_objects') || 'Erase objects'}</span>
                  </ActionsMenuItem>
                  {map.is_edited && (
                    <ActionsMenuItem onClick={() => handleRestoreClick(map)} disabled={isProcessing}>
                      <RestoreIcon className={style.menuItemIcon} />
                      <span className={style.menuItemText}>
                        {t('robot_map.restore_original') || 'Restore original map'}
                      </span>
                    </ActionsMenuItem>
                  )}
                  {canDeleteMap && (
                    <ActionsMenuItem onClick={() => setMapToDelete(map)} disabled={isProcessing}>
                      <RemoveSvg className={style.menuItemIcon} />
                      <span className={style.menuItemText}>{t('common.delete')}</span>
                    </ActionsMenuItem>
                  )}
                </ActionsMenu>
              </div>
            </div>
          ))
        ) : (
          <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-secondary)' }}>No maps found</div>
        )}
      </div>

      {mapToDelete && (
        <ConfirmDialog
          title={
            (mapToDelete.usage_count ?? 0) > 0 || (mapToDelete.used_in_missions?.length ?? 0) > 0 ? (
              <span>
                {t('robot_map.delete_in_use_warning_prefix', { filename: mapToDelete.name })}
                {mapToDelete.used_in_missions?.map((m, i) => (
                  <span key={m.id}>
                    <Link
                      to={`/mission/${m.id}`}
                      style={{ color: 'var(--link-color)', textDecoration: 'underline' }}
                      onClick={() => setMapToDelete(undefined)}
                    >
                      {m.name}
                    </Link>
                    {i < (mapToDelete.used_in_missions?.length ?? 0) - 1 ? ', ' : ''}
                  </span>
                ))}
                <br />
                {t('robot_map.delete_in_use_warning_suffix')}
              </span>
            ) : (
              t('view_zones.delete_question', { filename: mapToDelete.name })
            )
          }
          isOpen={!!mapToDelete}
          onClose={() => setMapToDelete(undefined)}
          onConfirmClick={() => {
            if ((mapToDelete.usage_count ?? 0) > 0 || (mapToDelete.used_in_missions?.length ?? 0) > 0) {
              setMapToDelete(undefined)
            } else {
              onDeleteMap(mapToDelete)
              setMapToDelete(undefined)
            }
          }}
          confirmText={
            (mapToDelete.usage_count ?? 0) > 0 || (mapToDelete.used_in_missions?.length ?? 0) > 0
              ? t('common.ok')
              : t('common.delete')
          }
          danger={!((mapToDelete.usage_count ?? 0) > 0 || (mapToDelete.used_in_missions?.length ?? 0) > 0)}
          showCancelButton={!((mapToDelete.usage_count ?? 0) > 0 || (mapToDelete.used_in_missions?.length ?? 0) > 0)}
        />
      )}

      {mapToRename && (
        <RenameRobotMapModal
          isOpen={!!mapToRename}
          mapName={mapToRename.name}
          mapId={mapToRename.id}
          onSave={onMapsChanged}
          onClose={() => setMapToRename(undefined)}
        />
      )}

      {mapToRestore && (
        <ConfirmDialog
          title={
            t('robot_map.restore_confirm') ||
            'Are you sure you want to restore the original map? This will delete all edited occupancy map data.'
          }
          isOpen={!!mapToRestore}
          onClose={() => setMapToRestore(undefined)}
          onConfirmClick={handleConfirmRestore}
          confirmText={t('common.confirm') || 'Confirm'}
          danger={true}
          showCancelButton={true}
        />
      )}
    </div>
  )
}

export default RobotMapTable
